import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck2, X } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { siteLabel } from '../../lib/sites';
import { formatSourceDate } from '../../lib/formatting';
import type { MeetingBriefItem } from '../../hooks/useTeamTodaySurface';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

/*
 * MeetingPrepModal — the A3 prep document (webapp-actions §6.5 / D-ACT29).
 *
 * TWO REQUESTS, DELIBERATELY. The FRAME (GET .../prep) is pure DB and lands in ~250ms: time, the full
 * agenda, per-attendee RSVP, the join link. The GROUNDED section (POST .../prep/grounding) calls the
 * query engine and can take tens of seconds. Splitting them is what makes "degrades to the frame alone"
 * true on screen, and it makes *loading* a state of its own that can never be mistaken for "nothing found".
 *
 * THE THREE GROUNDING STATES ARE NOT INTERCHANGEABLE:
 *   grounded  — we looked and found something. Prose + citable sources.
 *   abstained — we looked (or deliberately declined to) and there is nothing honest to say. NO retry
 *               button: retrying is guaranteed to produce the identical outcome and costs real spend.
 *   failed    — we could NOT look. Retry offered only when the backend says retrying could change the
 *               answer (`retryable`) — a 24h spend cap is not something a button can fix.
 *
 * The presence or absence of that retry button is the most testable expression of "an abstention is a
 * finding, not a failure", which is the distinction this whole surface is built around.
 */

interface PrepAttendee {
  name: string | null;
  email: string | null;
  responseStatus: string | null;
  optional: boolean;
  isTeammate: boolean;
}

interface PrepFrame {
  eventId: string;
  title: string | null;
  startAt: string;
  endAt: string | null;
  allDay: boolean;
  timeZone: string;
  location: string | null;
  meetingUrl: string | null;
  htmlLink: string | null;
  isOrganizer: boolean;
  yourResponse: string | null;
  agenda: string | null;
  attendeeCount: number;
  attendees: PrepAttendee[];
  recurringEventId: string | null;
  isRecurring: boolean;
}

interface PrepSource {
  n: number;
  id: string;
  title: string | null;
  site: string;
  snippet: string | null;
  updated: string | null;
  scope: 'team' | 'private';
}

interface PrepGrounding {
  state: 'grounded' | 'abstained' | 'failed';
  lookedFor: {
    question: string | null;
    since: string | null;
    until: string | null;
    people: string[];
    topics: string[];
    subjectSource: 'agenda' | 'title' | 'series' | 'none';
  };
  prose: string | null;
  sources: PrepSource[];
  citedSources: number;
  reason: 'no_subject_no_teammates' | 'no_recent_work' | 'nothing_captured' | null;
  failure: 'rate_limited' | 'error' | null;
  retryable: boolean;
}

// Each abstention names its OWN cause and its OWN remedy. An earlier draft used one message for two
// different states, which told a reader whose teammates had simply had a quiet fortnight that nobody in
// the meeting was on their team — false, and the fix it suggested ("invite them") was already done.
const ABSTAIN_COPY: Record<NonNullable<PrepGrounding['reason']>, string> = {
  no_subject_no_teammates:
    'This meeting doesn’t say what it’s about, and nobody in it is on your Terum team — anything here would be a guess. Add an agenda to the calendar event, or invite the teammates you’re syncing with.',
  no_recent_work:
    'This meeting doesn’t say what it’s about, and the teammates in it haven’t captured any work in this window — so there’s nothing to bring you up to speed on.',
  nothing_captured: 'Nothing in the team’s captured work touches this meeting yet.',
};

const RSVP_LABEL: Record<string, string> = {
  accepted: 'going',
  declined: 'declined',
  tentative: 'maybe',
  needsAction: 'no reply',
};

function meetingWhen(frame: PrepFrame): string | null {
  if (frame.allDay) return 'All day';
  const start = new Date(frame.startAt);
  if (Number.isNaN(start.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: frame.timeZone,
    }).format(start);
  } catch {
    return null;
  }
}

export function MeetingPrepModal({
  meeting,
  onClose,
  onNav,
}: {
  meeting: MeetingBriefItem;
  onClose: () => void;
  onNav?: (id: string) => void;
}) {
  useEscapeToClose(onClose);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const receiptId = useId();

  const frame = useQuery({
    queryKey: ['surfaces', 'meeting-prep', meeting.eventId],
    queryFn: () => apiFetch<PrepFrame>(`/api/surfaces/meetings/${encodeURIComponent(meeting.eventId)}/prep`),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // A QUERY around the grounding POST, deliberately (bug-536). The POST spends a full agent loop and one
  // of the reader's 20 queries/60s, so it must fire exactly ONCE per open — the old useMutation + fire-once
  // ref hand-rolled that guarantee and StrictMode's dev remount broke it (the remounted observer never saw
  // the first observer's result; the section hung on "Reading…" forever). The query cache dedupes the
  // double-mount into one network call and shares the result with whichever observer renders; staleTime
  // Infinity + retry:false + global refetchOnWindowFocus:false mean nothing ever re-fires it silently —
  // re-opening the modal within gcTime reuses the cached answer (LESS spend than before, never more). The
  // key does not start with 'surfaces', so no surface invalidation can re-trigger the spend; the retry
  // button below is an explicit refetch().
  const grounding = useQuery({
    queryKey: ['meeting-prep-grounding', meeting.eventId],
    queryFn: () =>
      apiFetch<PrepGrounding>(`/api/surfaces/meetings/${encodeURIComponent(meeting.eventId)}/prep/grounding`, {
        method: 'POST',
      }),
    staleTime: Infinity,
    retry: false,
  });

  const f = frame.data;
  const g = grounding.data;
  const when = f ? meetingWhen(f) : meeting.when ?? null;
  const joinHref = f ? f.meetingUrl ?? f.htmlLink : meeting.meetingUrl ?? meeting.htmlLink ?? null;

  // Chrome only: the approved dialog register (mock CardDialog / ConflictResolveModal). All rails,
  // states, and copy above are unchanged — including the deliberate no-retry-on-abstain rule.
  const sectionLabel = "font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground";
  const pill =
    'rounded-full border border-[#d7cab7] bg-[#fffaf2] px-3.5 py-1.5 text-sm transition hover:border-foreground dark:border-border dark:bg-card';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mp-title"
      className="rd fixed inset-0 z-50 flex items-end justify-center bg-[#2d2822]/35 p-3 font-['DM_Sans'] text-foreground backdrop-blur-[2px] sm:items-center sm:p-6"
      onMouseDown={onClose}
    >
      <section
        onMouseDown={(e) => e.stopPropagation()}
        className="mp-modal flex max-h-[92vh] w-full max-w-[620px] flex-col overflow-hidden rounded-[26px] border border-[#ded2c0] bg-popover shadow-[0_28px_80px_rgba(48,38,25,.25)] dark:border-border"
      >
        <div className="border-b border-[#e6dccd] px-5 pb-5 pt-5 sm:px-7 sm:pt-7 dark:border-border">
          <div className="flex items-start justify-between gap-5">
            <div className="flex items-start gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-acc-soft text-acc">
                <CalendarCheck2 size={19} />
              </span>
              <div>
                <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">Prep</p>
                <h2 id="mp-title" className="mt-1 text-[19px] font-semibold leading-snug tracking-[-.01em]">
                  {f?.title ?? meeting.title}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close dialog"
              className="grid size-9 shrink-0 place-items-center rounded-full border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              <X size={17} />
            </button>
          </div>
          {/* Body prose, not a label — text-prose-foreground (bug-615); muted measures 3.45:1 on the
            * popover in the cream default theme, under the 4.5:1 AA floor. */}
          {when && <p className="mt-4 text-[15px] leading-6 text-prose-foreground">{when}</p>}
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-7">
          {/* THE FRAME. apiFetch throws on any non-ok, so an error here is a real 404/500 and must say so —
              silently leaving the brief's 400-char teaser on screen would misrepresent it as the full agenda. */}
          {frame.isError ? (
            <p className="text-sm text-destructive">
              Couldn’t load this meeting — it may have been cancelled or moved out of the synced window.
            </p>
          ) : (
            f && (
              <>
                {f.agenda && (
                  <div>
                    <p className={sectionLabel}>Agenda</p>
                    <p className="mt-2 text-sm leading-6 text-prose-foreground">{f.agenda}</p>
                  </div>
                )}
                {(f.attendees?.length ?? 0) > 0 && (
                  <div>
                    <p className={sectionLabel}>Who’s coming</p>
                    <ul className="mt-2 space-y-1">
                      {(f.attendees ?? []).map((a, i) => (
                        <li
                          key={a.email ?? `att-${i}`}
                          className={`text-sm leading-6 ${a.isTeammate ? 'mp-att-team font-medium' : 'text-muted-foreground'}`}
                        >
                          {a.name ?? a.email ?? 'Unknown'}
                          {a.responseStatus && RSVP_LABEL[a.responseStatus] && (
                            <span className="text-muted-foreground"> · {RSVP_LABEL[a.responseStatus]}</span>
                          )}
                          {a.optional && <span className="text-muted-foreground"> · optional</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )
          )}

          {/* THE GROUNDED SECTION. */}
          <div className="rounded-2xl border border-[#e4dacb] bg-[#fffaf3] p-4 dark:border-border dark:bg-muted">
            <p className={sectionLabel}>What you should know going in</p>
            {grounding.isPending ? (
              <p className="mt-2 text-sm text-muted-foreground">Reading the team’s captured work…</p>
            ) : grounding.isError || !g ? (
              <>
                <p className="mt-2 text-sm text-destructive">
                  I couldn’t check the team’s work just now. Nothing here is missing on purpose.
                </p>
                <button className={`mt-3 ${pill}`} onClick={() => grounding.refetch()}>
                  Try again
                </button>
              </>
            ) : g.state === 'grounded' ? (
              <>
                <p className="mt-2 text-[15px] leading-6">{g.prose}</p>
                {g.sources.length > 0 && (
                  <ul className="mt-3 space-y-1 border-t border-black/[.06] pt-3 dark:border-white/10">
                    {g.sources.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          className="mp-source w-full rounded-lg px-1.5 py-1 text-left text-sm leading-5 transition hover:bg-black/[.04] dark:hover:bg-white/[.06]"
                          onClick={() =>
                            onNav?.(s.scope === 'private' ? `my-conversations/${s.id}` : `conversations/${s.id}`)
                          }
                        >
                          <span className="font-['DM_Mono'] text-[11px] text-acc">[{s.n}]</span> {s.title ?? 'Untitled'}
                          <span className="text-muted-foreground">
                            {' '}
                            · {siteLabel(s.site)}
                            {s.updated && ` · ${formatSourceDate(s.updated)}`}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : g.state === 'abstained' ? (
              // NO retry button, on purpose — see the header note.
              <p className="mt-2 text-sm text-muted-foreground">
                {g.reason ? ABSTAIN_COPY[g.reason] : 'There’s nothing to add here yet.'}
              </p>
            ) : (
              <>
                <p className="mt-2 text-sm text-destructive">
                  {g.failure === 'rate_limited'
                    ? 'You’ve hit the query limit. Nothing here is missing on purpose.'
                    : 'I couldn’t check the team’s work just now. Nothing here is missing on purpose.'}
                </p>
                {g.retryable && (
                  <button className={`mt-3 ${pill}`} onClick={() => grounding.refetch()}>
                    Try again
                  </button>
                )}
              </>
            )}
          </div>

          {/* THE RECEIPT. On a read-only surface with no rating widget, this is the reader's ONLY way to tell
              "there is nothing" from "you looked for the wrong thing" — and the only signal that tells us. */}
          {g?.lookedFor && (g.lookedFor.question || (g.lookedFor.people?.length ?? 0) > 0) && (
            <div>
              <button
                type="button"
                className="flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
                aria-expanded={receiptOpen}
                aria-controls={receiptId}
                onClick={() => setReceiptOpen((v) => !v)}
              >
                What I looked for <span aria-hidden="true">{receiptOpen ? '↑' : '↓'}</span>
              </button>
              {receiptOpen && (
                <div className="mt-2 space-y-1 text-sm text-muted-foreground" id={receiptId}>
                  {g.lookedFor.since && g.lookedFor.until && (
                    <p>
                      <span className={sectionLabel}>Window</span> {g.lookedFor.since} → {g.lookedFor.until}
                    </p>
                  )}
                  {(g.lookedFor.people?.length ?? 0) > 0 && (
                    <p>
                      <span className={sectionLabel}>People</span> {(g.lookedFor.people ?? []).join(', ')}
                    </p>
                  )}
                  {(g.lookedFor.topics?.length ?? 0) > 0 && (
                    <p>
                      <span className={sectionLabel}>Last time</span> {(g.lookedFor.topics ?? []).join('; ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[#e6dccd] bg-[#f6f0e6] px-5 py-4 sm:px-7 dark:border-border dark:bg-muted">
          {joinHref && (
            <a className={pill} href={joinHref} target="_blank" rel="noreferrer">
              {f?.meetingUrl ?? meeting.meetingUrl ? 'Join ↗' : 'Open ↗'}
            </a>
          )}
          <button
            className="rounded-lg bg-foreground px-4 py-2 text-sm text-card transition hover:opacity-90"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </section>
    </div>
  );
}
